import { UseQueryResult } from '@tanstack/react-query'
import { useCallback } from 'react'
import { useDispatch } from 'react-redux'

import { parseGroup } from '../../models/Group'
import useQueryParameters, { queryParameters } from '../../utilities/useQueryParameters'
import * as api from '../api'
import { useGroups } from '../entities'
import { groupActions } from '../store'
import { useApiQuery } from '../useApiQuery'
import { ReturnsGroups } from '../useInvalidate'

const groupFilterQueryParameters = queryParameters().number('page').string('groupsearch')

export default function useGroupFilter() {
  const dispatch = useDispatch()

  const { page, groupsearch, set } = useQueryParameters(groupFilterQueryParameters)
  const setPage = useCallback((page: number) => set({ page }, { preventScrollReset: true }), [set])
  const setSearch = useCallback(
    (search: string) => set({ groupsearch: search, page: 0 }, { preventScrollReset: true }),
    [set]
  )

  const result: UseQueryResult<{ pageCount: number; pageSize: number; itemCount: number; uuids: string[] }> =
    useApiQuery({
      queryKey: ['groupFilter', ReturnsGroups, groupsearch, page],
      placeholderData: (previousData) =>
        previousData || {
          pageCount: 1,
          pageSize: 15,
          itemCount: 8,
          uuids: ['dummy-1', 'dummy-2', 'dummy-3', 'dummy-4', 'dummy-5', 'dummy-6', 'dummy-7', 'dummy-8'],
        },
      queryFn: async () => {
        const res = await api.get(
          `group?page=${page + 1}&page_size=15&&search=${encodeURIComponent(groupsearch || '')}`
        )
        const groups = parseGroup.array(res.data)
        const pageCount = res.lastPage || 0
        const itemCount = res.total || 0
        dispatch(groupActions.add(groups))
        return { pageCount, pageSize: 15, itemCount, uuids: groups.map(({ uuid }) => uuid) }
      },
    })

  const { groups } = useGroups(result.data?.uuids || [])

  return {
    page,
    setPage,
    search: groupsearch,
    setSearch,
    groups,
    pageCount: result.data?.pageCount || 0,
    pageSize: result.data?.pageSize || 15,
    itemCount: result.data?.itemCount || 0,
    result,
  }
}
